import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import {
  getConsent,
  setConsent,
  grantAnalyticsConsent,
  denyAnalyticsConsent,
} from "@/lib/analytics";

const CookieBanner = () => {
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = getConsent();
    if (choice === "granted") {
      grantAnalyticsConsent();
      setVisible(false);
    } else if (choice === "denied") {
      denyAnalyticsConsent();
      setVisible(false);
    } else {
      setVisible(true);
    }

    // Allow re-opening from footer "Cookie-instellingen" link
    const openHandler = () => setVisible(true);
    window.addEventListener("wasz:open-cookie-banner", openHandler);
    return () => window.removeEventListener("wasz:open-cookie-banner", openHandler);
  }, []);

  const accept = () => {
    setConsent("granted");
    grantAnalyticsConsent();
    setVisible(false);
  };

  const decline = () => {
    setConsent("denied");
    denyAnalyticsConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label={lang === "nl" ? "Cookie-toestemming" : "Cookie consent"}
      className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6"
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-white/10 bg-[#0F0F23]/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] p-6 sm:p-7">
        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
          <div className="hidden sm:flex w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-white mb-1.5">
              {lang === "nl" ? "Cookies & analytics" : "Cookies & analytics"}
            </h2>
            <p className="text-sm text-white/65 leading-relaxed">
              {lang === "nl" ? (
                <>
                  Wij gebruiken Google Analytics om anoniem te meten hoe deze
                  website gebruikt wordt — zodat we de content kunnen
                  verbeteren. Er worden pas cookies geplaatst nadat u toestemming
                  geeft. Geen marketing, geen tracking voor derden. Meer
                  informatie in onze{" "}
                  <Link to="/cookies" className="text-[#00D4FF] hover:underline">
                    Cookieverklaring
                  </Link>{" "}
                  en{" "}
                  <Link to="/privacy" className="text-[#00D4FF] hover:underline">
                    Privacyverklaring
                  </Link>.
                </>
              ) : (
                <>
                  We use Google Analytics to anonymously measure how this site
                  is used so we can improve our content. No cookies are placed
                  until you give consent. No marketing, no third-party
                  tracking. Learn more in our{" "}
                  <Link to="/cookies" className="text-[#00D4FF] hover:underline">
                    Cookie Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-[#00D4FF] hover:underline">
                    Privacy Policy
                  </Link>.
                </>
              )}
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <button
                data-testid="cookie-accept"
                onClick={accept}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00D4FF] hover:bg-white text-[#050509] font-bold text-sm h-10 px-5 transition-colors"
              >
                {lang === "nl" ? "Accepteren" : "Accept"}
              </button>
              <button
                data-testid="cookie-decline"
                onClick={decline}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-transparent border border-white/20 hover:border-[#00D4FF] hover:text-[#00D4FF] text-white font-semibold text-sm h-10 px-5 transition-colors"
              >
                {lang === "nl" ? "Weigeren" : "Decline"}
              </button>
            </div>
          </div>
          <button
            data-testid="cookie-close"
            onClick={decline}
            aria-label={lang === "nl" ? "Sluiten en weigeren" : "Close and decline"}
            className="text-white/40 hover:text-white p-1 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
